import React, { FC, useState } from 'react'
import { useGetVersionQuery, VersionFragment } from '@sketch/gql-types'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import { useOnEvent } from '@sketch/utils'
import {
  GenericErrorView,
  LoadingPage,
  getRenderStatus,
} from '@sketch/modules-common'

import { extractVersionInformation } from './extractVersionInformation'
import {
  ShareVersionContext,
  ShareWithoutVersion,
  VersioningContext,
  VersioningContextBase,
} from './ShareVersionContext'
import {
  isUpgradeToLatestNeeded,
  UpgradeToLatestVersion,
  useUpgradeToLatestVersion,
} from './UpgradeToLatestVersion'
import { useVersionUpdateCallback } from './useVersionUpdateCallback'
import { useGetPendingPatches } from '../hooks/useGetPendingPatches'

export interface VersionUpdateHandlerProps
  extends Pick<RouteComponentProps, 'location' | 'history'> {
  shareIdentifier: string
  selectedVersionShortId: string
}

const VersionUpdateHandler: FC<VersionUpdateHandlerProps> = ({
  shareIdentifier,
  selectedVersionShortId,
  history,
  location,
}) => {
  const versionUpdateHandler = useVersionUpdateCallback({ location, history })
  useOnEvent('versionIsProcessed', share => {
    if (share.identifier === shareIdentifier) {
      versionUpdateHandler(share)
    }
  })

  const { upgradeToLatest } = useUpgradeToLatestVersion()
  useOnEvent('versionDeleted', ({ share, versionShortId, versionKind }) => {
    // in case user is looking at the version which just got deleted - upgrade to the latest version:
    // See for more details:
    // - https://github.com/sketch-hq/Cloud/issues/3892#issuecomment-657587833
    // - https://github.com/sketch-hq/Cloud/issues/4715
    if (
      share.identifier === shareIdentifier &&
      versionShortId === selectedVersionShortId
    ) {
      // We'll be automatically and aggressively pruning "auto" versions
      // and to avoid confusing users, we've opted to not show the toast
      // when upgrading them to the latest version.
      const skipToast = versionKind === 'AUTO'
      upgradeToLatest('deleted-version', skipToast)
    }
  })
  return null
}

export interface ShareVersionProviderProps extends RouteComponentProps {
  latestVersion: VersionFragment
  onVersionUpdateNeeded?: () => Promise<void>
  share: ShareWithoutVersion
}

export const ShareVersionProvider: FC<ShareVersionProviderProps> = ({
  location,
  onVersionUpdateNeeded,
  history,
  share,
  latestVersion,
  children,
}) => {
  const {
    matchKind,
    versionShortId: versionShortIdFromLocation,
  } = extractVersionInformation(location.pathname)

  // save the artboard thumbnail URL, so when we navigate into an artboard we can already load it
  const [artboardThumbnail, setArtboardThumbnail] = useState('')

  if (matchKind === 'none') {
    throw Error('The given location should be within the context of a document')
  }

  const latestVersionId = latestVersion.shortId!
  const versionShortId = versionShortIdFromLocation || latestVersionId
  const isViewingLatestVersion =
    matchKind === 'latest' ||
    (matchKind === 'specific' && versionShortIdFromLocation === latestVersionId)

  const hasPendingPatches = useGetPendingPatches({
    shareIdentifier: share.identifier,
  })

  const versioningContextBase: VersioningContextBase = {
    latestVersion,
    share,
    versionShortId,
    latestVersionId,
    onVersionUpdateNeeded: async () =>
      onVersionUpdateNeeded && (await onVersionUpdateNeeded()),
    isViewingLatestVersion,
    hasPendingPatches,
    setArtboardThumbnail,
    artboardThumbnail,
    renderStatus: null,
  }

  const { data, loading, error } = useGetVersionQuery({
    variables: { versionShortId, shareIdentifier: share.identifier },
    // Make sure the component re-renders when the network status changes
    notifyOnNetworkStatusChange: true,
  })

  const renderStatus = !loading
    ? getRenderStatus(data?.share?.version?.document?.renderStatus ?? null) ??
      null
    : null

  // TODO: Improve the status with a possible ERROR state
  // More context: https://sketch.slack.com/archives/C05STLR9PN0/p1696844738742039
  const presentationStatus =
    data?.share?.version?.presentationStatus ?? 'PENDING'

  const isProcessingPresentationFile = presentationStatus === 'PENDING'

  const shouldInvalidate =
    !!data?.share && data.share.version?.shortId !== versionShortId

  const value: VersioningContext =
    loading || !data?.share?.version || shouldInvalidate
      ? {
          ...versioningContextBase,
          loading: true,
        }
      : {
          ...versioningContextBase,
          loading: false,
          currentVersion: data.share.version,
          renderStatus,
        }

  if (isUpgradeToLatestNeeded(error)) {
    return (
      <ShareVersionContext.Provider value={value}>
        <UpgradeToLatestVersion error={error} />
      </ShareVersionContext.Provider>
    )
  }

  if (error || (!loading && !data?.share)) {
    return <GenericErrorView error={error} />
  }

  if (isProcessingPresentationFile) {
    return (
      <span data-testid="loading-presentation-file">
        <LoadingPage.Sidebar message="Generating..." />
      </span>
    )
  }

  return (
    <ShareVersionContext.Provider value={value}>
      <VersionUpdateHandler
        location={location}
        history={history}
        selectedVersionShortId={versionShortId}
        shareIdentifier={share.identifier}
      />
      {children}
    </ShareVersionContext.Provider>
  )
}

export default withRouter(ShareVersionProvider)
